import React from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATE_KEYS = [
  "aguascalientes",
  "baja_california",
  "baja_california_sur",
  "campeche",
  "chiapas",
  "chihuahua",
  "cdmx",
  "coahuila",
  "colima",
  "durango",
  "guanajuato",
  "guerrero",
  "hidalgo",
  "jalisco",
  "mexico",
  "michoacan",
  "morelos",
  "nayarit",
  "nuevo_leon",
  "oaxaca",
  "puebla",
  "queretaro",
  "quintana_roo",
  "san_luis_potosi",
  "sinaloa",
  "sonora",
  "tabasco",
  "tamaulipas",
  "tlaxcala",
  "veracruz",
  "yucatan",
  "zacatecas",
];

const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function LeadForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = React.useState({
    name: "",
    email: "",
    state: "",
  });
  const [errors, setErrors] = React.useState({});

  const onChange = (field) => (e) =>
    setValues((prev) => ({
      ...prev,
      [field]: e && e.target ? e.target.value : e,
    }));

  const onSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!values.name.trim()) next.name = t("hero.form_error_name");
    if (!validEmail(values.email)) next.email = t("hero.form_error_email");
    if (!values.state) next.state = t("hero.form_error_state");
    setErrors(next);
    if (Object.keys(next).length === 0) {
      navigate("/thank_you");
    }
  };

  return (
    <form
      id="solicitar"
      data-testid="lead-form"
      onSubmit={onSubmit}
      noValidate
      className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl ring-1 ring-slate-200"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[#059669]">
        {t("hero.eyebrow")}
      </p>
      <h3 className="mt-2 text-2xl font-bold text-slate-900 tracking-tight">
        {t("hero.form_title")}
      </h3>
      <p className="mt-2 text-sm text-slate-600">{t("hero.form_subtitle")}</p>

      <div className="mt-6 space-y-4">
        <div>
          <Label
            htmlFor="lead-name"
            className="text-sm font-semibold text-slate-800"
          >
            {t("hero.form_name_label")}
          </Label>
          <Input
            id="lead-name"
            data-testid="lead-name"
            className="mt-1.5 h-11 bg-white text-slate-900 placeholder:text-slate-400"
            placeholder={t("hero.form_name_placeholder")}
            value={values.name}
            onChange={onChange("name")}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-600" data-testid="lead-error-name">
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <Label
            htmlFor="lead-email"
            className="text-sm font-semibold text-slate-800"
          >
            {t("hero.form_email_label")}
          </Label>
          <Input
            id="lead-email"
            data-testid="lead-email"
            type="email"
            className="mt-1.5 h-11 bg-white text-slate-900 placeholder:text-slate-400"
            placeholder={t("hero.form_email_placeholder")}
            value={values.email}
            onChange={onChange("email")}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600" data-testid="lead-error-email">
              {errors.email}
            </p>
          )}
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-800">
            {t("hero.form_state_label")}
          </Label>
          <Select value={values.state} onValueChange={onChange("state")}>
            <SelectTrigger
              data-testid="lead-state"
              className="mt-1.5 h-11"
              aria-invalid={!!errors.state}
            >
              <SelectValue placeholder={t("hero.form_state_placeholder")} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {STATE_KEYS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`lead-state-${s}`}>
                  {t(`states.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && (
            <p className="mt-1 text-xs text-red-600" data-testid="lead-error-state">
              {errors.state}
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        data-testid="lead-submit"
        className="mt-6 h-12 w-full bg-[#059669] hover:bg-[#047857] text-white rounded-full text-base font-semibold"
      >
        {t("hero.form_submit")}
      </Button>
      <p className="mt-3 text-xs text-slate-500">{t("hero.form_privacy")}</p>
    </form>
  );
}
